import {
  SHADOW_TEAM_POSITION_GROUPS,
  normalizeShadowTeamPositionGroup,
} from "./formation";

const normalizeSearchValue = (value = "") =>
  String(value ?? "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const hasToken = (value, token) => {
  if (!value || !token) {
    return false;
  }

  return value.split(" ").includes(token);
};

const SEARCH_QUERY_GROUP_MATCHERS = [
  {
    group: SHADOW_TEAM_POSITION_GROUPS.GOALKEEPER,
    terms: ["goalkeeper", "goal keeper", "goalie", "keeper"],
  },
  {
    group: SHADOW_TEAM_POSITION_GROUPS.DEFENDER,
    terms: [
      "defender",
      "defence",
      "defense",
      "fullback",
      "full back",
      "wingback",
      "wing back",
      "center back",
      "centre back",
    ],
  },
  {
    group: SHADOW_TEAM_POSITION_GROUPS.MIDFIELDER,
    terms: [
      "midfielder",
      "midfield",
      "mid field",
      "defensive midfielder",
      "defensive mid",
      "attacking midfielder",
      "attacking mid",
      "central midfielder",
      "central mid",
      "playmaker",
    ],
  },
  {
    group: SHADOW_TEAM_POSITION_GROUPS.ATTACKER,
    terms: [
      "attacker",
      "forward",
      "striker",
      "winger",
      "attacking midfielder",
      "attacking mid",
    ],
  },
];

const ensurePositionEntries = (value) => {
  if (Array.isArray(value)) {
    return value.filter(Boolean);
  }

  return value ? [value] : [];
};

const ATTACKING_MIDFIELDER_GROUPS = [
  SHADOW_TEAM_POSITION_GROUPS.MIDFIELDER,
  SHADOW_TEAM_POSITION_GROUPS.ATTACKER,
];

const PLAYER_POSITION_GROUP_BY_CODE = {
  GK: SHADOW_TEAM_POSITION_GROUPS.GOALKEEPER,

  LB: SHADOW_TEAM_POSITION_GROUPS.DEFENDER,
  RB: SHADOW_TEAM_POSITION_GROUPS.DEFENDER,
  CB: SHADOW_TEAM_POSITION_GROUPS.DEFENDER,
  LCB: SHADOW_TEAM_POSITION_GROUPS.DEFENDER,
  RCB: SHADOW_TEAM_POSITION_GROUPS.DEFENDER,
  WB: SHADOW_TEAM_POSITION_GROUPS.DEFENDER,
  LWB: SHADOW_TEAM_POSITION_GROUPS.DEFENDER,
  RWB: SHADOW_TEAM_POSITION_GROUPS.DEFENDER,
  LD: SHADOW_TEAM_POSITION_GROUPS.DEFENDER,
  RD: SHADOW_TEAM_POSITION_GROUPS.DEFENDER,
  CD: SHADOW_TEAM_POSITION_GROUPS.DEFENDER,
  LCD: SHADOW_TEAM_POSITION_GROUPS.DEFENDER,
  RCD: SHADOW_TEAM_POSITION_GROUPS.DEFENDER,
  SW: SHADOW_TEAM_POSITION_GROUPS.DEFENDER,

  DM: SHADOW_TEAM_POSITION_GROUPS.MIDFIELDER,
  CDM: SHADOW_TEAM_POSITION_GROUPS.MIDFIELDER,
  LDM: SHADOW_TEAM_POSITION_GROUPS.MIDFIELDER,
  RDM: SHADOW_TEAM_POSITION_GROUPS.MIDFIELDER,
  CM: SHADOW_TEAM_POSITION_GROUPS.MIDFIELDER,
  LCM: SHADOW_TEAM_POSITION_GROUPS.MIDFIELDER,
  RCM: SHADOW_TEAM_POSITION_GROUPS.MIDFIELDER,
  CAM: ATTACKING_MIDFIELDER_GROUPS,
  LAM: ATTACKING_MIDFIELDER_GROUPS,
  RAM: ATTACKING_MIDFIELDER_GROUPS,
  LCAM: ATTACKING_MIDFIELDER_GROUPS,
  RCAM: ATTACKING_MIDFIELDER_GROUPS,
  LM: SHADOW_TEAM_POSITION_GROUPS.MIDFIELDER,
  RM: SHADOW_TEAM_POSITION_GROUPS.MIDFIELDER,
  AM: ATTACKING_MIDFIELDER_GROUPS,
  ACM: ATTACKING_MIDFIELDER_GROUPS,
  M: SHADOW_TEAM_POSITION_GROUPS.MIDFIELDER,

  ST: SHADOW_TEAM_POSITION_GROUPS.ATTACKER,
  CF: SHADOW_TEAM_POSITION_GROUPS.ATTACKER,
  LF: SHADOW_TEAM_POSITION_GROUPS.ATTACKER,
  RF: SHADOW_TEAM_POSITION_GROUPS.ATTACKER,
  LW: SHADOW_TEAM_POSITION_GROUPS.ATTACKER,
  RW: SHADOW_TEAM_POSITION_GROUPS.ATTACKER,
  SS: SHADOW_TEAM_POSITION_GROUPS.ATTACKER,
  FW: SHADOW_TEAM_POSITION_GROUPS.ATTACKER,
  FWD: SHADOW_TEAM_POSITION_GROUPS.ATTACKER,
  LCF: SHADOW_TEAM_POSITION_GROUPS.ATTACKER,
  RCF: SHADOW_TEAM_POSITION_GROUPS.ATTACKER,
};

const addPositionGroups = (groups, nextGroups) => {
  ensurePositionEntries(nextGroups).forEach((group) => {
    const normalizedGroup = normalizeShadowTeamPositionGroup(group);
    if (normalizedGroup) {
      groups.add(normalizedGroup);
    }
  });
};

const inferPositionGroupsFromValue = (value) => {
  const normalizedValue = normalizeSearchValue(value);

  if (!normalizedValue) {
    return [];
  }

  if (
    normalizedValue.includes("attacking midfielder") ||
    normalizedValue.includes("attacking mid")
  ) {
    return ATTACKING_MIDFIELDER_GROUPS;
  }

  const compactValue = normalizedValue.replace(/\s+/g, "").toUpperCase();
  if (PLAYER_POSITION_GROUP_BY_CODE[compactValue]) {
    return ensurePositionEntries(PLAYER_POSITION_GROUP_BY_CODE[compactValue]);
  }

  if (
    normalizedValue.includes("goalkeeper") ||
    normalizedValue.includes("goal keeper") ||
    hasToken(normalizedValue, "goalie") ||
    hasToken(normalizedValue, "keeper")
  ) {
    return [SHADOW_TEAM_POSITION_GROUPS.GOALKEEPER];
  }

  if (
    normalizedValue.includes("midfielder") ||
    normalizedValue.includes("midfield") ||
    normalizedValue.includes("playmaker")
  ) {
    return [SHADOW_TEAM_POSITION_GROUPS.MIDFIELDER];
  }

  if (
    normalizedValue.includes("wingback") ||
    normalizedValue.includes("wing back") ||
    normalizedValue.includes("fullback") ||
    normalizedValue.includes("full back") ||
    normalizedValue.includes("back") ||
    normalizedValue.includes("defender") ||
    normalizedValue.includes("defence") ||
    normalizedValue.includes("defense")
  ) {
    return [SHADOW_TEAM_POSITION_GROUPS.DEFENDER];
  }

  if (
    normalizedValue.includes("striker") ||
    normalizedValue.includes("forward") ||
    normalizedValue.includes("winger") ||
    normalizedValue.includes("attacker")
  ) {
    return [SHADOW_TEAM_POSITION_GROUPS.ATTACKER];
  }

  return [];
};

export const getShadowTeamSearchQueryGroups = (query) => {
  const normalizedQuery = normalizeSearchValue(query);
  const groups = new Set();

  if (!normalizedQuery) {
    return groups;
  }

  SEARCH_QUERY_GROUP_MATCHERS.forEach(({ group, terms }) => {
    if (
      terms.some((term) => {
        const normalizedTerm = normalizeSearchValue(term);
        return normalizedQuery.includes(normalizedTerm);
      })
    ) {
      groups.add(group);
    }
  });

  return groups;
};

export const getShadowTeamPlayerPositionGroups = (player) => {
  const groups = new Set();
  const positionEntries = [
    ...ensurePositionEntries(player?.positions),
    ...ensurePositionEntries(player?.position),
    ...ensurePositionEntries(player?.main_position),
    ...ensurePositionEntries(player?.mainPosition),
    ...ensurePositionEntries(player?.primary_position),
    ...ensurePositionEntries(player?.primaryPosition),
    player?.position_name,
    player?.positionName,
    player?.position_label,
    player?.positionLabel,
    player?.position_short_name,
    player?.positionShortName,
    player?.position_code,
    player?.positionCode,
    player?.lexic_id,
    player?.lexicId,
    player?.role,
    player?.role_name,
    player?.roleName,
    player?.role_code,
    player?.roleCode,
  ].filter(Boolean);

  positionEntries.forEach((positionEntry) => {
    if (typeof positionEntry === "string") {
      addPositionGroups(groups, inferPositionGroupsFromValue(positionEntry));
      return;
    }

    if (typeof positionEntry !== "object") {
      return;
    }

    [
      positionEntry.short_name,
      positionEntry.shortName,
      positionEntry.code,
      positionEntry.name,
      positionEntry.label,
      positionEntry.title,
      positionEntry.position_name,
      positionEntry.positionName,
      positionEntry.position_label,
      positionEntry.positionLabel,
      positionEntry.lexic_id,
      positionEntry.lexicId,
      positionEntry.role,
      positionEntry.role_name,
      positionEntry.roleName,
      positionEntry.role_code,
      positionEntry.roleCode,
    ].forEach((value) => {
      addPositionGroups(groups, inferPositionGroupsFromValue(value));
    });
  });

  return groups;
};

export const filterShadowTeamSearchResultsForSlot = (players = [], slot, query) => {
  return Array.isArray(players) ? players : [];
};
